/**
 * @file content/index.tsx
 * @description Main content listing page that displays blog posts with filtering and search capabilities.
 * Supports featured posts, side content, and responsive layouts.
 */
import { HttpError, useGo, useInvalidate, useList, useTranslation } from "@refinedev/core";
import { useAtomValue } from "jotai";
import { blogIdAtom } from "./state";
import { blogger_v3 } from "googleapis"
import { MultiselectInput } from "@components/molecules/MultiselectInput";
import { FaArrowRotateRight, FaTag, FaTrash } from "react-icons/fa6";
import { FaSearch, FaTimes } from "react-icons/fa";
import { Multiselect } from "@components/molecules/Multiselect";
import { SearchInput } from "@components/molecules/Search";
import { Controller, useForm } from "react-hook-form";
import { GoogleServicesTemplate } from "@components/templates/googleServices";
import { ContentCard } from "@components/organisms/ContentCard";
import { useState } from "react";

/**
 * Props for the ContentList component.
 */
export interface ContentListProps {

}

/**
 * Main content listing component that handles post display, filtering, and search functionality.
 */
export function ContentList(props: ContentListProps) {
    // Get the current blog ID from global state
    const blogId = useAtomValue(blogIdAtom)
    const { translate } = useTranslation()
    const go = useGo()
    const invalidate = useInvalidate()

    // State to control mobile search interface visibility
    const [isMobileSearchActive, setIsMobileSearchActive] = useState(false);

    /**
     * Form for handling search and label filtering in the navbar
     */
    const navbarFilterForm = useForm<{
        search: string,
        labels: string[]
    }>({
        defaultValues: {
            labels: [],
            search: ''
        }
    })

    // Extract form control and watch for filter changes
    const {
        watch,
        control,
        // register
        formState: { errors },
    } = navbarFilterForm
    const filters = watch()
    // logger.debug(filters)

    /**
     * Fetch regular posts with pagination and filtering
     */
    const {
        data: dataPost,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: 'blogger/blogs/:blogId/posts', //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            },
            fields: ['title', 'images', 'id', 'published', 'labels', 'content']
        },
        queryOptions: {
            enabled: !!blogId && filters.search == '',
        },
        // sorters: [
        //     {
        //         field: 'startTime',
        //         order: 'asc'
        //     }
        // ],
        pagination: {
            pageSize: 50,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'fetchImages',
                operator: 'eq',
                value: true
            },
            {
                field: 'labels',
                operator: 'eq',
                value: filters.labels,
            }
        ]
    })

    /**
     * Fetch posts based on search query
     */
    const {
        data: dataPostSearch,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: 'blogger/blogs/:blogId/posts/search', //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            },
            // fields: ['title', 'images', 'id', 'published', 'labels']
        },
        queryOptions: {
            enabled: !!blogId && filters.search != '',
        },
        pagination: {
            pageSize: 50,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'q',
                operator: 'eq',
                value: filters.search
            },
        ]
    })

    /**
     * Fetch featured posts for hero section
     */
    const {
        data: dataFeatured,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: 'blogger/blogs/:blogId/posts', //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            },
            fields: ['title', 'images', 'id', 'published', 'labels', 'content']
        },
        queryOptions: {
            enabled: !!blogId && filters.search == '',
        },
        // sorters: [
        //     {
        //         field: 'startTime',
        //         order: 'asc'
        //     }
        // ],
        pagination: {
            pageSize: 3,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'fetchImages',
                operator: 'eq',
                value: true
            },
            {
                field: 'labels',
                operator: 'eq',
                value: ['featured'],
            }
        ]
    })

    /**
     * Fetch side content posts for sidebar
     */
    const {
        data: dataSide,
        // isError,
        // isLoading,
        // hasNextPage,
        // fetchNextPage,
        // isFetchingNextPage,
    } = useList<blogger_v3.Schema$Post, HttpError, blogger_v3.Schema$Post>({
        dataProviderName: 'gapi',
        resource: 'blogger/blogs/:blogId/posts', //https://developers.google.com/calendar/api/v3/reference/events/list
        meta: {
            params: {
                blogId: blogId
            },
            fields: ['title', 'images', 'id', 'published', 'labels', 'content']
        },
        queryOptions: {
            enabled: !!blogId && filters.search == '',
        },
        // sorters: [
        //     {
        //         field: 'startTime',
        //         order: 'asc'
        //     }
        // ],
        pagination: {
            pageSize: 3,
        },
        filters: [
            {
                field: 'status',
                operator: 'eq',
                value: 'LIVE'
            },
            {
                field: 'fetchImages',
                operator: 'eq',
                value: true
            },
            {
                field: 'labels',
                operator: 'eq',
                value: ['side'],
            }
        ]
    })

    // Extract data from API responses
    const posts = dataPost?.data
    const postsSearchRaw = dataPostSearch?.data
    const postsFeatured = dataFeatured?.data
    const postsSide = dataSide?.data

    // Filter search results by selected labels
    const postsSearch = filters.labels.length > 0
        ? postsSearchRaw?.filter(p => filters.labels.every(l => p.labels?.includes(l)))
        : postsSearchRaw

    // logger.debug(posts)

    // Available label options for filtering
    const optionLabels = ['pro', 'health', 'other', 'yoga', 'featured']

    // if some options need to be obtained from API
    // const { selectProps: categorySelectProps } = useSelect<ICategory>({
    //     resource: "blogger/blogs/",
    //     optionLabel: "title",
    //     optionValue: "id",
    //     defaultValue: getDefaultFilter("category.id", filters, "in"),
    //   });

    // const { data: canShowContent } = useCan({
    //     resource: "content",
    //     action: "show",
    //     // params: { foo: "optional-params" },
    // });
    // logger.debug(canShowContent)

    /**
     * Function to invalidate and refresh post data
     */
    const invalidatePosts = async () => {
        await invalidate({
            dataProviderName: 'gapi',
            resource: 'blogger/blogs/:blogId/posts',
            invalidates: ['list']
        })
        await invalidate({
            dataProviderName: 'gapi',
            resource: 'blogger/blogs/:blogId/posts/search',
            invalidates: ['list']
        })
    }

    // logger.debug(posts)

    return <GoogleServicesTemplate
        /* Mobile search and filter interface */
        right={<div className="md:hidden">
            {isMobileSearchActive ? (
                <section className="flex w-full">
                    <Controller
                        control={control}
                        name="search"
                        rules={{
                            required: false,
                        }}
                        render={({ field }) => (
                            <SearchInput
                                className="grow input-bordered join-item absolute left-0 ml-4"
                                cleanIcon={FaTrash}
                                {...field}
                                autoFocus
                            />
                        )}
                    />
                    <button
                        className="btn btn-ghost join-item"
                        onClick={() => setIsMobileSearchActive(false)}
                    >
                        <FaTimes />
                    </button>
                </section>
            ) : (
                <section className="flex-1 join">
                    <button
                        className="btn join-item"
                        onClick={() => setIsMobileSearchActive(true)}
                    >
                        <FaSearch />
                    </button>
                    <Controller
                        control={control}
                        name="labels"
                        rules={{
                            required: true,
                        }}
                        render={({ field }) => (
                            <Multiselect
                                className="dropdown-left"
                                optionLabels={optionLabels}
                                {...field}
                            >
                                <button className="btn btn-primary flex-nowrap">
                                    <FaTag />
                                </button>
                            </Multiselect>
                        )}
                    />
                    <button className="btn btn-secondary join-item" onClick={invalidatePosts}>
                        <FaArrowRotateRight />
                    </button>
                </section>)}
        </div>}
        /* Desktop search and filter interface */
        subnavbar={<nav className="w-full hidden md:join">
            <Controller
                control={control}
                name="search"
                rules={{
                    required: true,
                }}
                render={({ field }) => (
                    <SearchInput className="grow input-bordered join-item" {...field} />
                )}
            />
            <Controller
                control={control}
                name="labels"
                rules={{
                    required: true,
                }}
                render={({ field }) => (
                    <MultiselectInput
                        label={<span className="flex gap-1 mr-1 items-center"><FaTag /></span>}
                        className="input-bordered join-item w-1/3"
                        optionLabels={optionLabels}
                        {...field}
                    />)}
            />
            <button className="btn btn-secondary join-item" onClick={invalidatePosts}>
                <FaArrowRotateRight />
            </button>
        </nav>}>
        {/* Main content area */}
        {filters.search == '' ? <>
            {/* Featured posts section */}
            {postsFeatured && <FeaturedPosts posts={postsFeatured} />}

            {/* Main content layout with posts and sidebar */}
            <section className="mt-12 flex">
                {/* Primary content area with responsive grid */}
                <article className="flex-wrap mx-2 flex gap-y-4 grow">
                    {posts?.map((post, i) => post && <ContentCard
                        key={post.id}
                        variant="responsive"
                        post={post}
                        className={'px-2 w-full md:w-1/2 lg:w-1/3'} />)}
                </article>

                {/* Sidebar with additional content */}
                <aside className="flex-wrap mx-2 hidden xl:flex flex-col gap-4 w-1/4">
                    <header className="prose">
                        <h2 className="">Featured Content</h2>
                    </header>
                    {postsSide?.map(post => post && <ContentCard key={post.id} variant="short" post={post} />)}
                </aside>
            </section>
        </> : <>
            {/* Search results layout */}
            <section className="mt-12 flex flex-col gap-4">
                {postsSearch?.map(post => post && <ContentCard key={post.id} variant="responsive" post={post} className="" />)}
            </section>
        </>
        }
    </GoogleServicesTemplate>
}

/**
 * Featured posts component that displays posts in a hero layout
 * @param posts - Array of featured posts to display
 */
const FeaturedPosts = ({ posts }: { posts: blogger_v3.Schema$Post[] }) => <section className="flex flex-col lg:flex-row px-6 my-4 gap-2 lg:max-h-[40vh]">
    {posts?.[0] && <ContentCard post={posts[0]} variant="hero" className={`lg:h-full w-full ${posts.length === 1 ? 'lg:w-full' : 'lg:w-1/2'}`} />}
    {posts.length > 1 && <article className="flex flex-col w-full lg:w-1/2 gap-2">
        {posts?.[1] && <ContentCard post={posts![1]} variant="hero" className={`w-full ${posts.length === 2 ? 'lg:h-full' : 'lg:h-1/2'}`} />}
        {posts?.[2] && <ContentCard post={posts![2]} variant="hero" className="w-full lg:h-1/2" />}
    </article>}
</section>
